import type { HealthComponent } from '@dooh/shared';

/** API base URL for CMS backend */
export const CMS_API_BASE = import.meta.env.VITE_CMS_API_URL ?? 'http://localhost:3100';

/** Player pairing states */
export type PlayerPairingState = 'unpaired' | 'pairing' | 'paired';

/** Result from POST /api/pairing/initiate */
export interface InitiatePairingResponse {
  deviceCode: string;
  userCode: string;
  verificationUri: string;
  expiresIn: number;
  interval: number;
}

/** Result from POST /api/pairing/complete */
export interface CompletePairingResponse {
  status: 'pending' | 'confirmed';
  message?: string;
  jwt?: string;
  screenId?: string;
  deviceId?: string;
  organizationId?: string;
}

/** Stored credentials after pairing */
export interface StoredCredentials {
  jwt: string;
  screenId: string;
  deviceId: string;
  organizationId: string;
}

// ─── MQTT Types ────────────────────────────────────────────────────────────────

/** AWS IoT Core MQTT endpoint */
export const AWS_IOT_ENDPOINT =
  import.meta.env.VITE_AWS_IOT_ENDPOINT ?? 'a13t5p0hkhkxql-ats.iot.us-east-1.amazonaws.com';

/** Result from POST /api/player/ws-url (SigV4-signed WSS URL) */
export interface WsUrlResponse {
  url: string;
  expiresAt: string; // ISO timestamp
}

/** MQTT connection states */
export type MqttConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting';

/**
 * Recycle-kiosk health block carried in the heartbeat, matching the shape
 * accepted by the shared `heartbeatInputSchema.recycle`. Absent entirely on
 * plain-signage screens (no recycle agent ever expected). `agent` reflects
 * the current/last-known WS connection state to the on-device recycle
 * agent: 'up' while connected, 'down' if it was ever connected and dropped,
 * 'absent' if a connection has never been established.
 */
export interface RecycleHeartbeatBlock {
  agent: 'up' | 'down' | 'absent';
  servo?: HealthComponent;
  dropSensor?: HealthComponent;
  nfc?: HealthComponent;
  binFillPercent?: number | null;
}

/** MQTT message received on subscribed topics */
export interface MqttMessage {
  topic: string;
  payload: Uint8Array;
}

/** Incoming command from CMS via MQTT */
export interface PlayerCommand {
  type: 'RELOAD_SCHEDULE' | 'REBOOT' | 'CLEAR_CACHE' | 'EMERGENCY' | 'CLEAR' | 'SLEEP' | 'WAKE';
  commandId?: string;
  timestamp?: string;
  assetId?: string; // for EMERGENCY
  duration?: number; // for EMERGENCY
  expiresAt?: string; // for EMERGENCY
  /** Overlay animation variant for SLEEP (defaults player-side when absent). */
  variant?: 'night-drift' | 'face';
  /**
   * Asset delivery metadata for EMERGENCY (#71) — lets the player render and
   * cache emergency creative that isn't already in the local cache. Absent on
   * commands from older CMS versions.
   */
  asset?: {
    url: string;
    checksum: string;
    fileSize: number;
    type: 'image' | 'video';
  };
}

/** Configuration for the MQTT client */
export interface MqttClientConfig {
  /** JWT for authenticating with CMS API */
  jwt: string;
  /** Device ID (used in topic paths and as MQTT clientId) */
  deviceId: string;
  /** Organization ID (used in topic paths) */
  organizationId: string;
  /** WSS URL refresh interval in milliseconds (default: 12h) */
  urlRefreshIntervalMs?: number;
  /** Base reconnect delay in ms (default: 1000) */
  reconnectBaseDelayMs?: number;
  /** Maximum reconnect delay in ms (default: 60000) */
  reconnectMaxDelayMs?: number;
  /** Maximum reconnect attempts (default: 20) */
  maxReconnectAttempts?: number;
}
